import { isInboundAvailable } from "@utils/postage";
import { Charity } from "reduxStore/services/api-v2/charities";
import { GlobalSettings } from "reduxStore/services/api-v2/globalSettings";

export type SetupProblem = {
  fieldId: string;
  message: string;
};

// Ids on the edit profile page that each problem links to
export const SETUP_FIELD_IDS = {
  active: "is-active-toggle",
  categories: "collection-categories",
  items: "items-needed",
  methods: "donation-methods",
  postal: "is-postal_service-active-toggle",
  dropoff: "is-dropoff_service-active-toggle",
  collection: "collection-phone",
  contact: "contact-details",
};

// Everything a charity still has to do before donors can give to it, in the order it appears on the edit profile page
export const getSetupProblems = (charity: Charity, globalSettings?: GlobalSettings): SetupProblem[] => {
  const problems: SetupProblem[] = [];
  const isCharity = !charity.is_retailer;

  if (isCharity && !charity.active) {
    problems.push({ fieldId: SETUP_FIELD_IDS.active, message: "Switch on \"Is active\" so donors can find your page" });
  }
  if (isCharity && charity.categories.length === 0) {
    problems.push({ fieldId: SETUP_FIELD_IDS.categories, message: "Choose at least one donation category, like Food or Toiletries" });
  }
  if (isCharity && Object.values(charity.items).filter(({ tier }) => tier > 0).length === 0) {
    problems.push({ fieldId: SETUP_FIELD_IDS.items, message: "Add at least one item you need, with its condition" });
  }
  if (isCharity && !(charity.postal_service.enabled || charity.collection_service.enabled || charity.dropoff_service.enabled)) {
    problems.push({ fieldId: SETUP_FIELD_IDS.methods, message: "Turn on at least one way for donors to give to you" });
  }
  // A donation method that is switched on but can't be used leaves donors with nothing to click
  if (charity.postal_service.enabled && globalSettings && !isInboundAvailable(charity, globalSettings)) {
    problems.push({
      fieldId: SETUP_FIELD_IDS.postal,
      message: "Switch Post off for now. Parcel delivery is not set up for your charity yet, so donors can't use it"
    });
  }
  if (charity.collection_service.enabled && !charity.collection_service.phone) {
    problems.push({
      fieldId: SETUP_FIELD_IDS.collection,
      message: "Add a phone number so donors can arrange a collection, or switch your own collection off"
    });
  }
  // Donors only see drop points that are switched on
  if (charity.dropoff_service.enabled && !charity.droppoints.some(droppoint => droppoint.user_enabled)) {
    problems.push({
      fieldId: SETUP_FIELD_IDS.dropoff,
      message: "Add or switch on a drop point so donors know where to bring items, or switch Drop-off off"
    });
  }
  if (!charity.website && !charity.contact_email && !charity.phone) {
    problems.push({ fieldId: SETUP_FIELD_IDS.contact, message: "Add a website, email or phone number so donors can get in touch" });
  }
  return problems;
};

export type SetupStep = {
  label: string;
  // Where on the edit profile page to fix it: the first problem, or the step's own section
  fieldId: string;
  done: boolean;
  problems: string[];
};

// The go-live checklist, built from the same checks as the problems above so the two always agree
const SETUP_STEPS = [
  { label: "Choose your donation categories", fieldIds: [ SETUP_FIELD_IDS.categories ] },
  { label: "Add the items you need", fieldIds: [ SETUP_FIELD_IDS.items ] },
  {
    label: "Set up a way for donors to give",
    fieldIds: [ SETUP_FIELD_IDS.methods, SETUP_FIELD_IDS.postal, SETUP_FIELD_IDS.collection, SETUP_FIELD_IDS.dropoff ]
  },
  { label: "Add your contact details", fieldIds: [ SETUP_FIELD_IDS.contact ] },
  { label: "Publish your page (switch on \"Is active\")", fieldIds: [ SETUP_FIELD_IDS.active ] },
];

export const getSetupSteps = (charity: Charity, globalSettings?: GlobalSettings): SetupStep[] => {
  const problems = getSetupProblems(charity, globalSettings);
  return SETUP_STEPS.map(({ label, fieldIds }) => {
    const stepProblems = problems.filter(problem => fieldIds.includes(problem.fieldId));
    return {
      label,
      fieldId: stepProblems[0]?.fieldId || fieldIds[0],
      done: stepProblems.length === 0,
      problems: stepProblems.map(({ message }) => message),
    };
  });
};
